import * as React from 'react';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLabel,
  Checkbox
} from '@bliks-io/ui';

export interface FormFieldCheckboxProps extends Omit<
  React.ComponentPropsWithoutRef<typeof Checkbox>,
  'onCheckedChange' | 'checked'
> {
  /**
   * Field label
   */
  label?: string;

  /**
   * Field description/helper text
   */
  description?: string;

  /**
   * Error message
   */
  error?: string;

  /**
   * Checked state
   */
  checked?: boolean;

  /**
   * Change handler
   */
  onCheckedChange?: (checked: boolean) => void;

  /**
   * Field ID
   */
  id?: string;

  /**
   * Field name
   */
  name?: string;

  /**
   * Field orientation (horizontal is recommended for checkboxes)
   */
  orientation?: 'vertical' | 'horizontal' | 'responsive';

  /**
   * Additional class for Field wrapper
   */
  fieldClassName?: string;
}

/**
 * Reusable form field component combining Field + Checkbox
 * Can be used standalone or wrapped by RHF adapter components
 * Uses horizontal orientation by default for better checkbox layout
 */
export const FormFieldCheckbox = React.forwardRef<
  React.ElementRef<typeof Checkbox>,
  FormFieldCheckboxProps
>(
  (
    {
      label,
      description,
      error,
      checked,
      onCheckedChange,
      id,
      name,
      orientation = 'horizontal',
      fieldClassName,
      ...checkboxProps
    },
    ref
  ) => {
    const fieldId = id || name;
    const hasError = !!error;

    return (
      <Field orientation={orientation} className={fieldClassName} data-invalid={hasError}>
        <Checkbox
          ref={ref}
          id={fieldId}
          checked={checked}
          onCheckedChange={onCheckedChange}
          aria-invalid={hasError}
          {...checkboxProps}
        />
        <FieldContent>
          {label && <FieldLabel htmlFor={fieldId}>{label}</FieldLabel>}
          {description && <FieldDescription>{description}</FieldDescription>}
          {error && <FieldError>{error}</FieldError>}
        </FieldContent>
      </Field>
    );
  }
);

FormFieldCheckbox.displayName = 'FormFieldCheckbox';
